import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { Vessel, VesselSearchResponse } from '@app-types/index';
import { environment } from '@utils/environment';

class HttpClientService {
  private apiClient: AxiosInstance;

  constructor() {
    this.apiClient = axios.create({
      baseURL: environment.baseUrl,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
  }

  /**
   * Get certificate status
   * @param query - Query parameter (id)
   * @param downloadPdf - Whether to download PDF
   * @returns Promise with Vessel data
   */
  async getCertificateStatus(query: string, downloadPdf: boolean, idType?: string): Promise<AxiosResponse<Vessel>> {
    return await this.apiClient.get('GetCertificates', {
      params: {
        id: query,
        pdf: downloadPdf,
        idType,
      },
    });
  }

  /**
   * Get PDF status
   * @param query - Query parameter (id)
   * @param downloadPdf - Whether to download PDF
   * @param uuid - UUID of the certificate
   * @returns Promise with Vessel data
   */
  async getPDFStatus(
    query: string,
    downloadPdf: boolean,
    uuid: string
  ): Promise<AxiosResponse<Vessel>> {
    return await this.apiClient.get('GetCertificatePdf', {
      params: {
        id: query,
        pdf: downloadPdf,
        uuid,
      },
    });
  }

  /**
   * Get vessel certificates
   * @param searchWord - Search word for vessels
   * @returns Promise with Vessel data
   */
  async getVesselCertificates(searchWord: string): Promise<AxiosResponse<VesselSearchResponse>> {
    return await this.apiClient.get('GetCertificates', {
      params: {
        id: searchWord,
        pdf: false,
      },
    });
  }

  /**
   * Generic GET request
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.apiClient.get<T>(url, config);
    return response.data;
  }

  /**
   * Generic POST request
   */
  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.apiClient.post<T>(url, data, config);
    return response.data;
  }

  /**
   * Generic PUT request
   */
  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.apiClient.put<T>(url, data, config);
    return response.data;
  }

  /**
   * Generic DELETE request
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.apiClient.delete<T>(url, config);
    return response.data;
  }
}

// Export singleton instance
export const httpClientService = new HttpClientService();
export default HttpClientService;
